import React, { useState, useEffect, useMemo, useRef } from "react";
import debounce from "lodash.debounce";
import MapView from "../components/MapView";
import FiltersPanel from "../components/FiltersPanel";
import ResultsList from "../components/ResultsList";
import SearchBar from "../components/SearchBar";
import NeighborhoodSummary from "../components/NeighborhoodSummary";
import AvailableBuildings from '../components/AvailableBuildings';
import "leaflet/dist/leaflet.css";
import "../styles/tenant.css";
import "../styles/layout.css";
import "../styles/buttons.css";
import "../styles/modals.css";

// API endpoint returns building summaries with nested Units array.

const API_BASE_URL = import.meta.env.VITE_API_URL;

// Custom hook that centralizes all building data management.
const useBuildingData = () => {
  const [results, setResults] = useState([]);  // Store all buildings
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [boundingBox, setBoundingBox] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [buildingDetailsCache, setBuildingDetailsCache] = useState({});
  const [detailedBuilding, setDetailedBuilding] = useState(null);
  const [selectedBuildingDetails, setSelectedBuildingDetails] = useState(null);


  
  const [filters, setFilters] = useState({
	  priceMin: 500,
	  priceMax: 10000,
	  bedrooms: 1,
	  bathrooms: 1,
	  amenities: {
		parking: false,
		gym: false,
		petFriendly: false,
	  },
	});
	
  const [sortOption, setSortOption] = useState("low-to-high");
  const [OpenedBuildingID, setOpenedBuildingID] = useState(null);

  const lastRequest = useRef({ bbox: null, search: "" });

  // ✅ Fetch buildings **only when necessary**
	const fetchBuildings = async (bbox, search) => {
	  try {
		let url = new URL (`${API_BASE_URL}/properties/building-summaries`);

		const normalizeBBox = (bbox) => ({
		  north: bbox.getNorth ? bbox.getNorth() : bbox.north,
		  south: bbox.getSouth ? bbox.getSouth() : bbox.south,
		  east: bbox.getEast ? bbox.getEast() : bbox.east,
		  west: bbox.getWest ? bbox.getWest() : bbox.west,
		});

		const { north, south, east, west } = normalizeBBox(bbox);

		const params = new URLSearchParams({
		  north,
		  south,
		  east,
		  west,
		  searchQuery: search || "",
		});


		if (search) params.append("searchQuery", search);
		url += `?${params.toString()}`;

		setLoading(true);
		const response = await fetch(`${url}`);
		if (!response.ok) throw new Error("Failed to fetch building summaries");

        const data = await response.json();
        setResults(data);
	  } catch (err) {
		console.error("Error fetching buildings:", err);
		setError(err);
	  } finally {
		setLoading(false);
	  }
	};

	const fetchBuildingDetails = async (buildingID) => {
	  if (buildingDetailsCache[buildingID]) {
		return buildingDetailsCache[buildingID]; // ✅ Return from cache
	  }

	  try {
		const response = await fetch(`${API_BASE_URL}/properties/buildings/${buildingID}/details`);
		if (!response.ok) throw new Error("Failed to fetch building details");

		const data = await response.json();

		setBuildingDetailsCache(prevCache => ({
		  ...prevCache,
		  [buildingID]: data, // ✅ Store in cache
		}));

		return data;
	  } catch (err) {
		console.error("Error fetching building details:", err);
		return null;
	  }
	};


  // ✅ Fetch Offer Statuses **every 10 seconds** (unchanged)
const fetchOfferStatuses = async () => {
    try {
        const response = await fetch(`${API_BASE_URL}/offers/status/all`); // ✅ New API route
        if (!response.ok) throw new Error("Failed to fetch offer statuses");

        const updatedOffers = await response.json();

        setResults(prevResults =>
            prevResults.map(building => {
                if (!building.Units || !Array.isArray(building.Units)) return building;

                const updatedUnits = building.Units.map(unit => {
                    const updatedOffer = updatedOffers.find(o => o.OfferID === unit.OfferID);
                    return updatedOffer ? { ...unit, AvailabilityStatus: updatedOffer.AvailabilityStatus } : unit;
                });

                return { ...building, Units: updatedUnits };
            })
        );
    } catch (err) {
        console.error("Error fetching offer statuses:", err);
    }
};


  // ✅ Fetch buildings only **once on page load**
	useEffect(() => {
	  const defaultBoundingBox = {
		getNorth: () => 42.40,
		getSouth: () => 42.30,
		getEast: () => -71.00,
		getWest: () => -71.20
	  };

	  fetchBuildings(defaultBoundingBox, "");  // Load initial buildings
	  setBoundingBox(defaultBoundingBox);
	}, []);

  
	useEffect(() => {
		if (!OpenedBuildingID) return;

		let isMounted = true;

		const fetchFullUnitDetails = async () => {
			try {
				console.log(`Fetching units for BuildingID: ${OpenedBuildingID}`);

				const response = await fetch(`${API_BASE_URL}/properties/units/${OpenedBuildingID}/details`);
				if (!response.ok) throw new Error(`Server Error: ${response.status} ${response.statusText}`);

				const unitData = await response.json();
				console.log("Fetched Unit Data:", unitData);

				const updatedUnitData = unitData.map(unit => ({
					...unit,
					Features: unit.Features || [],
					ImageGallery: unit.ImageGallery || []
				}));

				if (isMounted) {
					setSelectedBuildingDetails(prevBuilding => {
						console.log("Previous Building Data Before Update:", prevBuilding);
						console.log("Updated Units Data:", updatedUnitData);

						const newBuildingData = {
							...prevBuilding,
							Units: [...updatedUnitData]  // ✅ Ensuring a new reference to force re-render
						};

						console.log("New Building Data:", newBuildingData);
						return newBuildingData;
					});
				}
			} catch (error) {
				console.error("Error fetching full unit details:", error);
			}
		};

		fetchFullUnitDetails();

		return () => { isMounted = false; };
	}, [OpenedBuildingID]);

  // ✅ Fetch offer statuses **every 10 seconds** (unchanged)
  useEffect(() => {
    const interval = setInterval(fetchOfferStatuses, 10000);
    return () => clearInterval(interval);
  }, []);

	const filteredResults = useMemo(() => {
		if (!results) return [];

		return results.filter(building => (
			(!filters.priceMin || building.MinRent >= filters.priceMin) &&
			(!filters.priceMax || building.MaxRent <= filters.priceMax) &&
			(!filters.bedrooms || building.Units.some(unit => unit.Bedrooms >= filters.bedrooms)) &&
			(!filters.bathrooms || building.Units.some(unit => unit.Bathrooms >= filters.bathrooms))
		));
	}, [results, filters]);

  return {
    results,
	filteredResults, // ✅ Filtered results for UI
    loading,
    error,
    boundingBox,
    setBoundingBox,
    searchQuery,
    setSearchQuery,
    filters,
    setFilters,
    sortOption,
    setSortOption,
	filtersOpen,
	setFiltersOpen,
	fetchBuildingDetails,
	OpenedBuildingID,
	setOpenedBuildingID,
	detailedBuilding, 
	setDetailedBuilding,
	selectedBuildingDetails, 
	setSelectedBuildingDetails
  };
};

const TenantPage = () => {
  // Use the custom hook to centralize data management.
  const {
    filteredResults,
    loading,
    error,
    setBoundingBox,
    searchQuery,
    setSearchQuery,
	filtersOpen,
	setFiltersOpen,
    filters,
    setFilters,
    sortOption,
    setSortOption,
    loadMoreData,
	fetchBuildingDetails,
	OpenedBuildingID,
	setOpenedBuildingID,
	detailedBuilding, 
	setDetailedBuilding,
	selectedBuildingDetails, 
	setSelectedBuildingDetails
  } = useBuildingData();

  // UI state for synchronizing selections between MapView and ResultsList.
  const [highlightedBuilding, setHighlightedBuilding] = useState(null);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [selectedNeighborhood, setSelectedNeighborhood] = useState(null);
  const [currentZoom, setCurrentZoom] = useState(13);  // Track zoom here as well
  const UNIT_DISPLAY_ZOOM = 16;

  

  // When a building is highlighted (e.g. via hover in ResultsList or marker click in MapView).
  const highlightBuilding = buildingID => {
    setHighlightedBuilding(buildingID);
  };
 
  const handleBuildingClick = async (buildingID) => {
	  const details = await fetchBuildingDetails(buildingID);
	  if (details) {
		setSelectedBuildingDetails(details);
	  }
	};


  // Handle the search form submission.
  const handleSearch = e => {
    e.preventDefault();
    loadMoreData({ searchQuery });
  };

  return (
    <div className="tenant-view-container">
      {/* Map Section */}
      <div className="map-container">
        <MapView
          results={filteredResults}
          setBoundingBox={setBoundingBox}
          filters={filters}
          highlightBuilding={highlightBuilding}
		  handleBuildingClick={handleBuildingClick}
		  detailedBuilding={selectedBuildingDetails} 
		  setDetailedBuilding={setDetailedBuilding}
		  setOpenedBuildingID = {setOpenedBuildingID}
		  setCurrentZoom ={setCurrentZoom}
		  currentZoom={currentZoom}
		  setSelectedNeighborhood={setSelectedNeighborhood}
          loadMoreData={loadMoreData}  // Child can ask for more data if needed.
        />
      </div>

      {/* Filters and Results Section */}
      <div className="filters-results">
        <div className="filters-header-container">

             <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} handleSearch={() => fetchBuildings(null, searchQuery)} />

            <hr className="filters-divider" />

            <div className="filters-toggle-container">
			  <button className="filters-btn"  onClick={() => setFiltersOpen(!filtersOpen)}>
				{filtersOpen ? "Hide Filters" : "Show Filters"}
			  </button>
			  
            </div>
			<hr className="filters-divider" />
        </div>

        {/* Filters Panel */}
		{filtersOpen && (
		  <div className="filters-container">
			<FiltersPanel filters={filters} setFilters={setFilters} filtersOpen={filtersOpen} />
		  </div>
		)}

        {/* Results Section */}
        <div className="results-container">
		  {currentZoom < UNIT_DISPLAY_ZOOM ? (
			<NeighborhoodSummary selectedNeighborhood={selectedNeighborhood} />
		  ) : (
			<AvailableBuildings
			  results={filteredResults}
			  highlightedBuilding={highlightedBuilding}
			  setSelectedBuilding={setSelectedBuilding}
			  loadMoreData={loadMoreData}
			/>
		  )}
		</div>
      </div>
    </div>
  );
};

export default TenantPage;
